import { toast } from 'react-hot-toast';
import logger from '../../utils/logger';
import videoChunkService from '../../services/videoChunkService';

/** Tarayıcı/codec uyumsuzluğunda MediaRecorder oluşturmayı güvenli dener */
function createMediaRecorderForStream(stream) {
    if (!stream || !window.MediaRecorder) return null;
    if (!stream.getTracks().some((t) => t.readyState === 'live')) {
        logger.warn('MediaRecorder: aktif medya track yok');
        return null;
    }
    const candidates = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm'
    ];
    const canCheck = typeof MediaRecorder.isTypeSupported === 'function';
    for (const mimeType of candidates) {
        if (canCheck && !MediaRecorder.isTypeSupported(mimeType)) continue;
        try {
            return new MediaRecorder(stream, { mimeType });
        } catch (_) {
            /* bir sonraki codec */
        }
    }
    try {
        return new MediaRecorder(stream);
    } catch (e) {
        logger.error('MediaRecorder oluşturulamadı:', e);
        return null;
    }
}

// 2026-07-30: kayıt artık TEK bir Blob'da tarayıcı belleğinde birikmiyor — periyodik
// parçalar (chunk) halinde AKARKEN sunucuya yükleniyor (bellek sınırlı kalır, tab
// çökerse KVKK zorunlu kayıt kaybolmaz — bkz. media-service/controllers/
// videoChunkController.js + workers/videoChunkFinalizeWorker.js). 60sn — bellek
// sınırlı, istek sıklığı makul.
const CHUNK_INTERVAL_MS = 60000;

/** VideoRecorder.js'in eski findCustomerIdNumber()'ının store-erişimli eşdeğeri —
 * artık kayıt BAŞLARKEN (upload sırasında değil) müşteri ID'sine ihtiyaç var. */
function findCustomerIdNumber(state) {
    if (state.userType === 'customer' && state.userData) {
        return state.userData.idNumber || state.userData.customerId || state.userData.id || null;
    }
    if (state.userType === 'agent' && state.activeCall) {
        return state.activeCall.idNumber || state.activeCall.customerId || state.activeCall.customerIdNumber || null;
    }
    return null;
}

const createRecordingSlice = (set, get) => ({
    // Remote kayıt durumu
    isRecording: false,
    recordedVideo: null,

    // Local kayıt durumu
    isLocalRecording: false,
    localRecordedVideo: null,

    // Remote kayıt durumunu ayarlama
    setIsRecording: (isRecording) => set({ isRecording }),

    // Local kayıt durumunu ayarlama
    setIsLocalRecording: (isLocalRecording) => set({ isLocalRecording }),

    // Remote kaydedilen videoyu ayarlama
    setRecordedVideo: (recordedVideo) => set({ recordedVideo }),

    // Local kaydedilen videoyu ayarlama
    setLocalRecordedVideo: (localRecordedVideo) => set({ localRecordedVideo }),

    // Remote kaydı başlatma — artık async: önce sunucuda bir upload oturumu açıyor
    // (initUpload), sonra MediaRecorder'ı chunk-akışına göre kuruyor.
    startRecording: async (mediaStream) => {
        if (!mediaStream || !window.MediaRecorder) {
            return false;
        }

        try {
            const state = get();
            const { uploadId } = await videoChunkService.initUpload({
                sessionId: state.sessionId,
                recordingType: 'remote',
                fileName: `gorusme_${state.sessionId || 'isimsiz'}_${new Date().toISOString().replace(/:/g, '-')}_remote.webm`,
                mimeType: 'video/webm',
                customerIdNumber: findCustomerIdNumber(state)
            });

            const mediaRecorder = createMediaRecorderForStream(mediaStream);
            if (!mediaRecorder || typeof mediaRecorder.start !== 'function') {
                logger.error('Remote MediaRecorder oluşturulamadı');
                return false;
            }

            let chunkIndex = 0;
            // [ChatGPT-review #2/#8] Sıralı yükleme kuyruğu — chunk'lar paralel değil,
            // bir öncekinin isteği bitmeden bir sonraki başlamadan gider. Blob referansı
            // upload promise'i çözülünce bırakılır (biriktirilmez).
            let queue = Promise.resolve();

            mediaRecorder.onerror = (event) => {
                logger.error('Remote MediaRecorder hatası:', event.error);
                set({ isRecording: false });
            };

            mediaRecorder.ondataavailable = (event) => {
                if (!event.data || event.data.size === 0) return;
                const blob = event.data;
                const index = chunkIndex++;
                queue = queue
                    .then(() => videoChunkService.uploadChunk(uploadId, index, blob))
                    .catch((err) => logger.error(`Remote chunk ${index} yükleme hatası:`, err));
            };

            // [ChatGPT-review #8] stop() çağrılınca tarayıcı son dataavailable'ı tetikler
            // (o da kuyruğa zincirlenir) — "son chunk gerçekten S3'e ulaştı mı" garantisi
            // için complete() ancak kuyruk TAMAMEN boşalınca çağrılıyor.
            mediaRecorder.onstop = async () => {
                set({ isRecording: false });
                try {
                    await queue;
                    const result = await videoChunkService.complete(uploadId);
                    set({ recordedVideo: result.presignedUrl });
                } catch (err) {
                    logger.error('Remote kayıt tamamlama hatası:', err);
                }
            };

            mediaRecorder.start(CHUNK_INTERVAL_MS);
            set({ isRecording: true });

            return mediaRecorder;

        } catch (error) {
            logger.error('Remote kayıt başlatılırken hata:', error);
            return false;
        }
    },

    // Local kaydı başlatma — remote ile aynı chunk akışı.
    startLocalRecording: async (mediaStream) => {
        if (!mediaStream || !window.MediaRecorder) {
            return false;
        }

        try {
            const state = get();
            const { uploadId } = await videoChunkService.initUpload({
                sessionId: state.sessionId,
                recordingType: 'local',
                fileName: `gorusme_${state.sessionId || 'isimsiz'}_${new Date().toISOString().replace(/:/g, '-')}_local.webm`,
                mimeType: 'video/webm',
                customerIdNumber: findCustomerIdNumber(state)
            });

            const mediaRecorder = createMediaRecorderForStream(mediaStream);
            if (!mediaRecorder || typeof mediaRecorder.start !== 'function') {
                logger.error('Local MediaRecorder oluşturulamadı');
                return false;
            }

            let chunkIndex = 0;
            let queue = Promise.resolve();

            mediaRecorder.onerror = (event) => {
                logger.error('Local MediaRecorder hatası:', event.error);
                set({ isLocalRecording: false });
            };

            mediaRecorder.ondataavailable = (event) => {
                if (!event.data || event.data.size === 0) return;
                const blob = event.data;
                const index = chunkIndex++;
                queue = queue
                    .then(() => videoChunkService.uploadChunk(uploadId, index, blob))
                    .catch((err) => logger.error(`Local chunk ${index} yükleme hatası:`, err));
            };

            mediaRecorder.onstop = async () => {
                set({ isLocalRecording: false });
                try {
                    await queue;
                    const result = await videoChunkService.complete(uploadId);
                    set({ localRecordedVideo: result.presignedUrl });
                } catch (err) {
                    logger.error('Local kayıt tamamlama hatası:', err);
                }
            };

            mediaRecorder.start(CHUNK_INTERVAL_MS);
            set({ isLocalRecording: true });

            return mediaRecorder;

        } catch (error) {
            logger.error('Local kayıt başlatılırken hata:', error);
            return false;
        }
    },

    // Remote kaydı durdurma — onstop handler'ı yukarıdaki async tamamlama zincirini tetikler.
    stopRecording: (mediaRecorder) => {
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
            toast('Remote kayıt durduruluyor...');
        }
    },

    // Local kaydı durdurma
    stopLocalRecording: (mediaRecorder) => {
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
            toast('Local kayıt durduruluyor...');
        }
    },

    // Remote kaydedilen videoyu indirme — recordedVideo artık sunucudan imzalı bir
    // media-proxy URL'si (bkz. s3Uploader.js#signMediaUrl), local blob DEĞİL.
    downloadRecordedVideo: () => {
        const { recordedVideo } = get();

        if (!recordedVideo) {
            return;
        }

        try {
            const a = document.createElement('a');
            a.href = recordedVideo;
            a.download = `remote-video-kaydı-${new Date().toISOString().replace(/:/g, '-')}.webm`;
            a.click();
        } catch (error) {
            logger.error('Remote video indirme hatası:', error);
        }
    },

    // Local kaydedilen videoyu indirme
    downloadLocalRecordedVideo: () => {
        const { localRecordedVideo } = get();

        if (!localRecordedVideo) {
            return;
        }

        try {
            const a = document.createElement('a');
            a.href = localRecordedVideo;
            a.download = `local-video-kaydı-${new Date().toISOString().replace(/:/g, '-')}.webm`;
            a.click();
        } catch (error) {
            logger.error('Local video indirme hatası:', error);
        }
    },

    // ✅ Recording state'lerini temizleme
    clearRecordingState: () => {
        set({
            recordedVideo: null,
            localRecordedVideo: null,
            isRecording: false,
            isLocalRecording: false
        });
        toast('Video kayıt durumu temizlendi');
    },

    // ✅ Sadece remote recording state'ini temizleme
    clearRemoteRecordingState: () => {
        set({
            recordedVideo: null,
            isRecording: false
        });
    },

    // ✅ Sadece local recording state'ini temizleme
    clearLocalRecordingState: () => {
        set({
            localRecordedVideo: null,
            isLocalRecording: false
        });
    }
});

export default createRecordingSlice;
